import useContent from "@lib/use_content";
import { useState } from "react";
import ConcentricLoader from "@hudson/ui/components/Loader/ConcentricLoader";
import ContinueButton from "@components/ContinueButton";

import DebugInfo from "@components/Debug/Info";
import { useRouter } from "next/router";
import { FormProvider, useForm } from "react-hook-form";
import Error from "@hudson/ui/components/Error";
import Field from "@hudson/ui/components/FormFields/Field";
import Input from "@hudson/ui/components/FormFields/Input";
import FrameConfig from "@lib/classes/FrameConfig";

interface GroupLoginProps {
  className?: string;
  onSuccessfulLogin: (profileType?: string, successMsg?: string) => void;
}

export default function GroupLogin({ className = "", onSuccessfulLogin }: GroupLoginProps) {
  const router = useRouter();

  const { data: contentData, loading } = useContent([
    "beErrFieldRequired",
    "beErrGroupLogin",
    "beGroupLoginCodeLabel",
    "beGroupLoginSubmitBtn",
  ]);

  const formMethods = useForm();
  const [debugMessage, setDebugMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  interface LoginData {
    code: string;
  }

  async function submitForm(data: LoginData) {
    setErrorMessage("");
    setDebugMessage("");
    setSubmitting(true);

    const frameConfig = new FrameConfig();
    let { profileType, error } = await frameConfig.setGroupCode(data.code);

    if (error == "PASSWORD_NEEDED") {
      if (router.query.widget) {
        await router.replace(`/${router.query.siteId}/group_password?code=${data.code}&widget=1`);
      } else {
        await router.replace(`/${router.query.siteId}/group_password?code=${data.code}`);
      }
    } else if (error) {
      setErrorMessage(contentData?.content.beErrGroupLogin);
      setDebugMessage(error);
      setSubmitting(false);
    } else {
      onSuccessfulLogin(profileType);
    }
  }

  return loading ? (
    <ConcentricLoader className="opacity-60 max-w-2xl mx-auto  px-4 sm:py-10 sm:px-6 lg:px-8" />
  ) : (
    <FormProvider {...formMethods}>
      <form
        className="pt-6 flex flex-col items-center gap-y-6 sm:gap-x-4 mx-auto max-w-base"
        id="login"
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          formMethods.handleSubmit(submitForm)();
        }}
        noValidate
      >
        <div className={className}>
          {errorMessage && (
            <div className="relative">
              <Error className="mt-2" message={errorMessage} heavyStyle scrollTo />
              {debugMessage && (
                <DebugInfo className="absolute right-5 -top-3" dialogContent={debugMessage}>
                  Why?
                </DebugInfo>
              )}
            </div>
          )}
          <div className="flex gap-x-4  pb-4">
            <Field
              errorMessage={errorMessage}
              fieldControl={(value, onChange, inError, onBlur) => (
                <Input
                  autoComplete="username"
                  autoFocus={true}
                  className="w-40"
                  disabled={submitting}
                  inError={inError}
                  onBlur={onBlur}
                  onChange={onChange}
                  value={value}
                />
              )}
              fieldName="code"
              fieldValidations={{ required: contentData?.content.beErrFieldRequired }}
              label={contentData?.content.beGroupLoginCodeLabel}
            />

            <ContinueButton
              alignLeft
              className="justify-self-start"
              disabled={submitting}
              label={contentData?.content.beGroupLoginSubmitBtn}
              loading={submitting}
            />
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
